import { ApolloClient } from '@apollo/client'
import gql from 'graphql-tag'
import cloneDeep from 'lodash/cloneDeep'

import { PagePresentation, PageType, SingleProductQuery as SingleProductQueryType, SingleProductQueryVariables } from '@Generated/graphql'
import { SplitTests } from '@Globals/SplitTests/types'
import BlockContent from '@Queries/cms/fragments/BlockContent.fragment'
import Fields from '@Queries/cms/fragments/Fields.fragment'
import LinkedProducts from '@Queries/cms/fragments/LinkedProducts.fragment'
import MoneyContent from '@Queries/cms/fragments/MoneyContent.fragment'
import OpenGraphContent from '@Queries/cms/fragments/OpenGraphContent.fragment'
import PersonalizationOptions from '@Queries/cms/fragments/PersonalizationOptions.fragment'
import ProductBundle from '@Queries/cms/fragments/ProductBundle.fragment'
import ProductContent from '@Queries/cms/fragments/ProductContent.fragment'
import ProductCrossSellAndPersonalizationContent from '@Queries/cms/fragments/ProductCrossSellAndPersonalizationContent.fragment'
import ProductReviews from '@Queries/cms/fragments/ProductReviews.fragment'
import ProductSpecificationFragment from '@Queries/cms/fragments/ProductSpecification.fragment'
import ReferenceContent from '@Queries/cms/fragments/ReferenceContent.fragment'
import ProductPageTacticFragment from '@Queries/products/fragments/ProductPageTactic.fragment'
import ProductTile from '@Queries/products/tile.fragment'
import WordbookFragment from '@Queries/wordbook/wordbook.fragment'

export const SingleProductQuery = gql`
  ${BlockContent}
  ${Fields}
  ${LinkedProducts}
  ${MoneyContent}
  ${OpenGraphContent}
  ${PersonalizationOptions}
  ${ProductBundle}
  ${ProductContent}
  ${ProductCrossSellAndPersonalizationContent}
  ${ProductPageTacticFragment}
  ${ProductReviews}
  ${ProductSpecificationFragment}
  ${ProductTile}
  ${ReferenceContent}
  ${WordbookFragment}

  query SingleProduct(
    $type: PageType!
    $slug: String!
    $id: ID
    $reviewLimit: Int = 10
    $reviewCursor: String = ""
    $breadcrumbsVersion: Int = 1
    $presentation: PagePresentation = FULL
    $loadRecommendations: Boolean = false
    $boost: ID
  ) {
    page(type: $type, slug: $slug, id: $id, presentation: $presentation) {
      id
      type
      title
      url
      seo {
        title
        description
        noIndex
        includeInSitemap
      }
      breadcrumbs(version: $breadcrumbsVersion) {
        id
        type
        title
        url
      }
      openGraph {
        ...OpenGraphContent
      }
      alternateLinks {
        locale
        url
      }
      blocks {
        ...BlockContent
        ... on GlobalBlock {
          id
          type
          blocks {
            ...BlockContent
          }
        }
        ... on ProductListBlock {
          extraBlocks {
            ...BlockContent
          }
          injections {
            id
            slot
            blocks {
              ...BlockContent
            }
          }
          guidedFilters {
            ... on GuidedFilterAttributeEnum {
              items {
                extraBlocks {
                  ...BlockContent
                }
                injections {
                  id
                  slot
                  blocks {
                    ...BlockContent
                  }
                }
              }
            }
          }
        }
      }
      fields {
        ... on ProductField {
          key
          product(mode: PROJECTION, combineVariants: true, onlyEnabled: true, sortVariants: true) {
            ...ProductContent
            ...ProductReviews
            ...ProductSpecificationFragment
            tactics {
              gold {
                ...ProductPageTacticFragment
              }
              silver {
                ...ProductPageTacticFragment
              }
              bronze {
                ...ProductPageTacticFragment
              }
              temporary {
                ...ProductPageTacticFragment
              }
            }
            categoryIDs
            masterVariant {
              bundleContent: attribute(key: "products") {
                ...ProductBundle
              }
              bundlePrice: attributePrice(key: "products") {
                difference {
                  ...MoneyContent
                }
                originalPrice {
                  ...MoneyContent
                }
                price {
                  ...MoneyContent
                }
              }
              crossSells: attribute(key: "crossSells") {
                ... on ProductAttribute {
                  products(onlyAvailable: true) {
                    ...ProductCrossSellAndPersonalizationContent
                  }
                }
              }
              personalizations: attribute(key: "personalizations") {
                ... on ProductAttribute {
                  products {
                    ...ProductCrossSellAndPersonalizationContent
                  }
                }
              }
              linkingDataColors: attribute(key: "primaryColors", locale: "en-GB") {
                ... on BaseAttribute {
                  value
                }
              }
              linkingDataMaterials: attribute(key: "primaryMaterials", locale: "en-GB") {
                ... on BaseAttribute {
                  value
                }
              }
              linkingDataPrice: price {
                currencyCode
                format(mode: NUMBER_PLAIN, locale: "en-GB")
                discount {
                  format(mode: NUMBER_PLAIN, locale: "en-GB")
                }
              }
            }
            linkedProducts {
              ...LinkedProducts
            }
            houseBrand {
              products(mode: PROJECTION, onlyAvailable: true, onlyEnabled: true) {
                ...ProductTile
              }
            }
          }
        }
      }
    }
  }
`

export const getSingleProduct = async (
  apolloClient: ApolloClient<object>,
  slug: string,
  id: string = '',
  reviewLimit = 10,
  reviewCursor = '',
  presentation: PagePresentation = PagePresentation.Full,
  boost?: string,
  splitTests?: SplitTests | null,
) => {
  const response = await apolloClient.query<SingleProductQueryType, SingleProductQueryVariables>({
    query: SingleProductQuery,
    variables: {
      boost,
      breadcrumbsVersion: splitTests?.breadcrumbs_v3?.group === 'b' ? 3 : 2,
      id,
      presentation,
      slug,
      type: PageType.Product,
      reviewLimit,
      reviewCursor,
    },
  })

  // TODO: hack to avoid apollo-client's frozen objects in dev mode
  if (Object.isFrozen(response?.data)) {
    // eslint-disable-next-line no-param-reassign
    return cloneDeep(response)
  }

  return response
}
